import React, { useState, useEffect } from "react";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import { AfDateRangePicker } from "components/Input/AfDateRangePicker";
import { Button, Card, CardBody, CardHeader, Chip, Spinner } from "@heroui/react";
import { MagnifyingGlassIcon, DocumentArrowDownIcon, ArrowUpIcon, ArrowDownIcon, ArrowsUpDownIcon } from "@heroicons/react/24/outline";
import { utils, write } from "xlsx";
import { saveAs } from "file-saver";
import { ListCommanWay } from "API/commonApi";

const ProductWiseReport: React.FC = () => {
  const [startDate, setStartDate] = useState<string>(moment().subtract(10, "days").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState<string>(moment().endOf("day").format("YYYY-MM-DD"));
  const [tableData, setTableData] = useState<any[]>([]);
  const [productHeaders, setProductHeaders] = useState<string[]>([]);
  const [productKeys, setProductKeys] = useState<string[]>([]);
  const [productMap, setProductMap] = useState<Record<string, string>>({});
  const [sortStat, setSortStat] = useState<any>({ booking_date: "desc" }); // Default sort by date descending
  const [query, setQuery] = useState<any>({
    startDate: moment().subtract(10, "days").format("YYYY-MM-DD"),
    endDate: moment().endOf("day").format("YYYY-MM-DD"),
  });

  // Fetch product list to map product_code to product_name
  const { data: productListData } = useQuery(
    ["product_list"],
    () => ListCommanWay("/admin/product/list", {}, {}),
    {
      onSuccess(data) {
        if (data.status === "SUCCESS" && data.data) {
          const map: Record<string, string> = {};
          data.data?.data?.forEach((item: any) => {
            map[item.product_code] = item.product_name;
          });
          setProductMap(map);
        }
      },
    }
  );

  const { isFetching, isLoading, refetch } = useQuery(
    ["product_wise_report", query],
    () => ListCommanWay("/admin/booking/product-wise-summary", { ...query }, {}),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status === "SUCCESS" && data.data) {
          // Get unique products sorted
          const uniqueProducts = [...new Set(data.data.map((item: any) => item.product_name))].sort() as string[];
          setProductHeaders(uniqueProducts);
          setProductKeys(uniqueProducts.map((_, i) => `p${i + 1}`));

          // Get unique dates sorted
          const uniqueDates = [...new Set(data.data.map((item: any) => item.booking_date))].sort() as string[];

          // Group data by date and pivot products
          const dateMap: Record<string, any> = {};
          uniqueDates.forEach((date) => {
            const formattedDate = date ? moment(date).format("YYYY-MM-DD") : "No Date";
            if (!dateMap[formattedDate]) {
              dateMap[formattedDate] = {
                booking_date: formattedDate,
                display_date: date ? moment(date).format("DD MMM YY") : "No Date",
                day_name: date ? moment(date).format("ddd") : "-",
              };
              // Initialize all product columns to 0
              uniqueProducts.forEach((_, i) => {
                dateMap[formattedDate][`p${i + 1}`] = 0;
                dateMap[formattedDate][`p${i + 1}_shipments`] = 0;
              });
            }
          });

          // Fill in the values
          data.data.forEach((item: any) => {
            const formattedDate = item.booking_date ? moment(item.booking_date).format("YYYY-MM-DD") : "No Date";
            const productIndex = uniqueProducts.indexOf(item.product_name);
            if (productIndex >= 0 && dateMap[formattedDate]) {
              const total = (Number(item.total_base_value) || 0) + (Number(item.total_tax) || 0);
              dateMap[formattedDate][`p${productIndex + 1}`] += total;
              dateMap[formattedDate][`p${productIndex + 1}_shipments`] += Number(item.total_shipments) || 0;
            }
          });

          // Calculate row totals
          Object.values(dateMap).forEach((row: any) => {
            row.total = 0;
            row.total_shipments = 0;
            uniqueProducts.forEach((_, i) => {
              row.total += row[`p${i + 1}`] || 0;
              row.total_shipments += row[`p${i + 1}_shipments`] || 0;
            });
          });

          // Default sort by date descending
          const sortedData = Object.values(dateMap).sort((a: any, b: any) => {
            if (a.booking_date === "No Date") return 1;
            if (b.booking_date === "No Date") return -1;
            return moment(b.booking_date).valueOf() - moment(a.booking_date).valueOf();
          });
          setTableData(sortedData);
          setSortStat({ booking_date: "desc" });
        } else {
          setTableData([]);
        }
      },
      onError: () => {
        setTableData([]);
      },
    }
  );

  // Helper to get full product name from code
  const getProductName = (code: string) => {
    return productMap[code] || code;
  };

  const handleSort = (key: string) => {
    let data = [...tableData];
    let order = sortStat[key] === "desc" ? "asc" : "desc";

    const sortedData = data.sort((a, b) => {
      if (key === "booking_date") {
        if (a.booking_date === "No Date") return order === "asc" ? 1 : -1;
        if (b.booking_date === "No Date") return order === "asc" ? -1 : 1;
        return order === "asc"
          ? moment(a.booking_date).valueOf() - moment(b.booking_date).valueOf()
          : moment(b.booking_date).valueOf() - moment(a.booking_date).valueOf();
      }

      const valueA = a[key] || 0;
      const valueB = b[key] || 0;

      if (typeof valueA === "number" && typeof valueB === "number") {
        return order === "asc" ? valueA - valueB : valueB - valueA;
      }
      if (valueA < valueB) return order === "asc" ? -1 : 1;
      if (valueA > valueB) return order === "asc" ? 1 : -1;
      return 0;
    });

    setSortStat({ [key]: order });
    setTableData(sortedData);
  };

  const GET_SORT_ICON = (key: string) => {
    switch (sortStat[key]) {
      case "asc":
        return <ArrowUpIcon className="h-3 w-3 inline-block ml-1" />;
      case "desc":
        return <ArrowDownIcon className="h-3 w-3 inline-block ml-1" />;
      default:
        return <ArrowsUpDownIcon className="h-3 w-3 inline-block ml-1 opacity-40" />;
    }
  };

  const handleSubmit = () => {
    setQuery({
      startDate: startDate,
      endDate: endDate,
    });
  };

  const exportToExcel = () => {
    const exportData = tableData.map((row, index) => {
      const rowData: any = {
        "Sr.": index + 1,
        "Day": row.day_name,
        "Date": row.display_date,
      };
      productHeaders.forEach((header, i) => {
        rowData[getProductName(header)] = row[`p${i + 1}`] || 0;
      });
      rowData["Total"] = row.total || 0;
      return rowData;
    });
    const ws = utils.json_to_sheet(exportData);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Product Wise Report");
    const wbout = write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "ProductWiseReport.xlsx");
  };

  const getTotals = () => {
    const totals: Record<string, number> = { total: 0 };
    productKeys.forEach((key) => {
      totals[key] = tableData.reduce((acc, row) => acc + (row[key] || 0), 0);
      totals.total += totals[key];
    });
    return totals;
  };

  const totals = getTotals();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 font-inter">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-700 via-emerald-600 to-emerald-700 px-6 pt-2 pb-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            
            <div>
              <h1 className="text-xl font-bold text-white">Product Wise Report</h1>
              <p className="text-white/60 text-xs">-</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="px-4 -mt-4">
        <Card className="shadow-xl border-none overflow-visible">
          <CardBody className="p-4 overflow-visible">
            <div className="flex flex-wrap items-end gap-3">
              <AfDateRangePicker
                label="Date Range"
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
              />

              <Button
                size="sm"
                variant="flat"
                color="primary"
                className="h-8 mt-5"
                startContent={<DocumentArrowDownIcon className="w-4 h-4" />}
                onPress={exportToExcel}
              >
                Export
              </Button>
              <Button
                size="sm"
                color="success"
                className="h-8 mt-5"
                onPress={handleSubmit}
                isLoading={isLoading || isFetching}
                startContent={!isLoading && !isFetching && <MagnifyingGlassIcon className="w-4 h-4" />}
              >
                Search
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Results Table */}
      <div className="px-4 mt-4 pb-8">
        <Card className="shadow-xl border-none overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </span>
              <h2 className="text-lg font-bold text-slate-800">Product Summary</h2>
              {tableData.length > 0 && (
                <Chip size="sm" variant="flat" color="success" className="ml-2">
                  {tableData.length} records
                </Chip>
              )}
            </div>
          </CardHeader>
          <CardBody className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider border-b border-slate-200">Sr.</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider border-b border-slate-200">Day</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider border-b border-slate-200 cursor-pointer hover:bg-slate-100" onClick={() => handleSort("booking_date")}>Date {GET_SORT_ICON("booking_date")}</th>
                  {productHeaders.map((header, i) => (
                    <th
                      key={i}
                      className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider border-b border-slate-200 cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort(`p${i + 1}`)}
                    >
                      {getProductName(header)} {GET_SORT_ICON(`p${i + 1}`)}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider border-b border-slate-200 cursor-pointer hover:bg-slate-100" onClick={() => handleSort("total")}>Total {GET_SORT_ICON("total")}</th>
                </tr>
              </thead>
              <tbody>
                {(isLoading || isFetching) && (
                  <tr>
                    <td colSpan={4 + productHeaders.length} className="text-center py-12">
                      <Spinner size="lg" color="success" />
                      <p className="text-slate-500 mt-2">Loading data...</p>
                    </td>
                  </tr>
                )}
                {!isLoading && !isFetching && !tableData.length && (
                  <tr>
                    <td colSpan={4 + productHeaders.length} className="text-center py-12">
                      <div className="flex flex-col items-center">
                        <svg className="w-16 h-16 text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                        <p className="text-slate-500 font-medium">No data found</p>
                        <p className="text-slate-400 text-sm">Try adjusting your date range</p>
                      </div>
                    </td>
                  </tr>
                )}
                {tableData.map((row, index) => (
                  <tr key={row.booking_date || index} className="hover:bg-slate-50 transition-colors border-b border-slate-200">
                    <td className="px-4 py-3 text-slate-500 border-r border-slate-100">{index + 1}</td>
                    <td className="px-4 py-3 text-slate-600 border-r border-slate-100">{row.day_name}</td>
                    <td className="px-4 py-3 font-medium text-slate-800 border-r border-slate-100">{row.display_date}</td>
                    {productKeys.map((key, i) => (
                      <td key={i} className="px-4 py-3 text-right font-semibold text-slate-700 border-r border-slate-100">
                        {(row[key] || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right font-semibold text-green-600">
                      {(row.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
                {tableData.length > 0 && (
                  <tr className="bg-slate-100 font-bold">
                    <td className="px-4 py-3 border-r border-slate-200" colSpan={3}>Total</td>
                    {productKeys.map((key, i) => (
                      <td key={i} className="px-4 py-3 text-right border-r border-slate-200">
                        {(totals[key] || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right text-green-700">
                      {(totals.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default ProductWiseReport;
